import {
  AwaitableStream,
  MessageDirection,
  Sub,
  SubProperty,
  SubsSchema,
  TIncomingMessage,
  TOutgoingMessage,
  TPageStyles,
  unwrap,
  wrap,
} from "@superwall-me/schema"
import { toast } from "app/core/components/hints"
import { EElementEvent } from "app/paywallBuilderV2/components/Navigator/Element"
import { PaywallPreviewVaraibles } from "app/paywalls/helpers/types"
import { TPaywallTemplate } from "app/schema/paywallTemplate/paywallTemplate"
import { debug as debugMain } from "debug"
import _ from "lodash"
import { nanoid } from "nanoid"
import {
  Dispatch,
  Reducer,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react"
import { z } from "zod"
import { useSpecificPaywallContext } from "../components/forms/editorV3/contexts/PaywallContext"

export interface IPaywallVariables {
  /**
   * Substititions for for the preview to be rendered by paywall.js
   */
  subs: Sub[]
  /**
   * controlls if free trial is viewed by the user
   * @default false
   */
  viewFreeTrial: boolean
  /**
   * varaibles to populate the liquid template
   */
  variables: PaywallPreviewVaraibles
  /**
   * Handles global element color changes
   */
  pageStyles: TPageStyles
  pageStylesFromDB?: TPageStyles
  setIframeRef?: Dispatch<SetStateAction<RefObject<HTMLIFrameElement | null>>>
}

interface IPaywallPreviewLifecycle extends IPaywallVariables {
  previewUrl: string
  iFrameRenderKey: string | undefined
  setPaywallSubs: Dispatch<Sub[]> | undefined
  setSubstitutionsFromPing: Dispatch<Sub[]> | undefined
  enableHoverAndClick?: boolean
  updatePageStyles: (val: TPageStyles) => void
  setTemplateJson: Dispatch<SetStateAction<TPaywallTemplate | null>>
  setIframeRef: Dispatch<SetStateAction<RefObject<HTMLIFrameElement | null>>>
  isSubstitutionFocused: boolean
  selectedSubstitutionKey?: string
  setSelectedSubstitutionKey?: Dispatch<SetStateAction<string | undefined>>
  hoverSubstitutionKey?: string | null
}

export enum PreviewState {
  Loading = "loading",
  Loaded = "loaded",
  Failed = "failed",
}

type LoadingStep = "waiting-for-iframe" | "waiting-for-postmessage"

type PreviewStateLoading = {
  state: PreviewState.Loading
  step: LoadingStep
  message?: string
}

type PreviewStateLoaded = {
  state: PreviewState.Loaded
}

type PreviewStateFailed = {
  state: PreviewState.Failed
  message?: string
}

type LivePreviewState = PreviewStateLoading | PreviewStateLoaded | PreviewStateFailed

type State = {
  previewState: LivePreviewState
  trackingTimeout: string | undefined
}

type Action =
  | { key: "begin_loading" }
  | { key: "update_loading_step"; step: LoadingStep }
  | { key: "begin_loading_tracked_timeout"; timeoutKey: string }
  | { key: "check_timeout"; timeoutKey: string }
  | { key: "successfully_loaded" }

export enum EPreviewEvent {
  MOUSE_OVER_EDITOR_ELEMENT = "MOUSE_OVER_EDITOR_ELEMENT",
}

export type TMouseOverEditorElementEvent = CustomEvent<{
  key: string
}>

export type TClickPreviewElementEvent = CustomEvent<{
  key: string
}>

const reducer: Reducer<State, Action> = (state: State, action: Action): State => {
  switch (action.key) {
    case "begin_loading": {
      return {
        ...state,
        trackingTimeout: undefined,
        previewState: {
          state: PreviewState.Loading,
          step: "waiting-for-iframe",
        },
      }
    }
    case "update_loading_step": {
      if (state.previewState.state === PreviewState.Loading) {
        return {
          ...state,
          previewState: {
            state: PreviewState.Loading,
            step: action.step,
          },
        }
      }
      return state
    }
    case "begin_loading_tracked_timeout": {
      return {
        ...state,
        trackingTimeout: action.timeoutKey,
      }
    }
    case "check_timeout": {
      if (
        state.trackingTimeout === action.timeoutKey &&
        state.previewState.state === PreviewState.Loading
      ) {
        toast.error({ content: "Timed out. Is Paywall.js installed?" })
        return {
          ...state,
          trackingTimeout: undefined,
          previewState: {
            state: PreviewState.Failed,
            message:
              "Timed out waiting for SDK, please make sure your page has Paywall.js installed.",
          },
        }
      }
    }
    case "successfully_loaded": {
      return {
        ...state,
        previewState: {
          state: PreviewState.Loaded,
        },
      }
    }
  }
}

const debug = debugMain("paywall")

const debuggedReducer = <S, A>(reducer: Reducer<S, A>): Reducer<S, A> => {
  return (state: S, action: A): S => {
    debug("before", action, state)
    const updatedState = reducer(state, action)
    debug("after", updatedState)
    return updatedState
  }
}

function usePaywallPreviewReducer() {
  return useReducer(debuggedReducer(reducer), {
    trackingTimeout: undefined,
    previewState: {
      state: PreviewState.Loading,
      step: "waiting-for-iframe",
    },
  })
}

/**
 * manages the lifecycle of events sent to the iFrame
 * whenever props are updated, they are automatically sent to the iFrame
 * but when the iFrame is completely refreshed, republish() needs to be called again
 */
export function usePublishToIFrameLifecycle({
  subs,
  viewFreeTrial,
  variables,
  pageStyles,
  setIframeRef,
}: IPaywallVariables) {
  const iFrameRef = useRef<HTMLIFrameElement>(null)
  useEffect(() => {
    setIframeRef && setIframeRef(iFrameRef)
  }, [setIframeRef])

  const { selectedSubstitutionKey } = useSpecificPaywallContext({
    selectedSubstitutionKey: true,
  })

  const publish = useCallback((message: TIncomingMessage) => {
    const wrappedMessage = wrap(message, MessageDirection.ToPaywall)
    try {
      iFrameRef.current?.contentWindow?.postMessage(wrappedMessage, "*")
    } catch (e) {
      debug("failed to post message", e)
    }
  }, [])

  const publishTemplateSubstitutions = useCallback(() => {
    const formattedSubs = _.map(subs, (sub) =>
      sub.key === selectedSubstitutionKey ? { ...sub, isSelected: true } : sub
    )
    publish({
      event_name: "template_substitutions",
      substitutions: formattedSubs,
    })
  }, [publish, subs, selectedSubstitutionKey])

  const publishPrefix = useCallback(() => {
    publish({
      event_name: "template_substitutions_prefix",
      prefix: viewFreeTrial ? "freeTrial" : null,
    })
  }, [viewFreeTrial, publish])

  const publishTemplateVariables = useCallback(() => {
    publish({
      event_name: "template_variables",
      variables,
    })
  }, [publish, variables])

  const publishPageStyles = useCallback(() => {
    publish({
      event_name: "page_styles",
      pageStyles,
    })
  }, [publish, pageStyles])

  // when props are individually updated, immediately publish to iframe
  useEffect(publishTemplateSubstitutions, [publishTemplateSubstitutions])
  useEffect(publishPrefix, [publishPrefix])
  useEffect(publishTemplateVariables, [publishTemplateVariables])
  useEffect(publishPageStyles, [publishPageStyles])

  // helper function to publish all variables to the iframe
  // should be called whenever the iFrame is reloaded and ready
  const republish = useCallback(() => {
    publishTemplateSubstitutions()
    publishPrefix()
    publishTemplateVariables()
    publishPageStyles()
  }, [publishTemplateSubstitutions, publishPrefix, publishTemplateVariables, publishPageStyles])

  return { iFrameRef, republish, publish }
}

const addInitialSubStyles = (subs: Sub[]) => {
  return _.map(subs, (sub) => {
    if (!_.isEmpty(sub?.initialStyles)) {
      return {
        ...sub,
        properties: [
          ...(sub?.properties || []),
          {
            prefix: "default",
            property: {
              type: "styles",
              styles: Object.entries(sub?.initialStyles || {}).map(([property, value]) => ({
                property: _.kebabCase(property),
                value,
              })),
            },
          } as z.infer<typeof SubProperty>,
        ],
      }
    }
    return sub
  })
}

/**
 * manages the lifecycle of events sent back from the iFrame
 */
export function useHandleEventsFromIframeLifecycle({
  setPaywallSubs,
  setSubstitutionsFromPing,
  republish,
  setSelectedSubstitutionKey,
  updatePageStyles,
  pageStylesFromDB,
  setTemplateJson,
}: {
  setPaywallSubs: Dispatch<Sub[]> | undefined
  setSubstitutionsFromPing: Dispatch<Sub[]> | undefined
  republish: () => void
  setSelectedSubstitutionKey?: Dispatch<SetStateAction<string | undefined>>
  updatePageStyles: (val: TPageStyles) => void
  pageStylesFromDB: TPageStyles | undefined
  setTemplateJson: Dispatch<SetStateAction<TPaywallTemplate | null>>
}) {
  const [state, dispatch] = usePaywallPreviewReducer()

  const handleEvent = useCallback(
    (event: MessageEvent) => {
      const messages = unwrap<TOutgoingMessage>(event.data as string, MessageDirection.FromPaywall)
      messages.forEach((message) => {
        // @ts-ignore
        if (window && window.paywallTesting && window.paywallTesting.pjsEventStream) {
          ;(
            (window as any).paywallTesting.pjsEventStream as AwaitableStream<TOutgoingMessage>
          ).emit(message)
        }

        switch (message.event_name) {
          case "ping": {
            dispatch({ key: "successfully_loaded" })
            republish()
            const maybeSubs = SubsSchema.safeParse(message.substitutions)
            const styledSubs = addInitialSubStyles(maybeSubs.success ? maybeSubs.data : [])
            setPaywallSubs?.(styledSubs)
            setSubstitutionsFromPing?.(styledSubs)

            /**
             * Handle initial page styles
             */
            const unrecognizedStyles =
              (message?.page_styles || []).filter((style) => {
                if (!pageStylesFromDB || pageStylesFromDB.length === 0) return style

                return !pageStylesFromDB.find(
                  (s) => s.property === style.property && s.from === style.from
                )
              }) || []
            const newPageStyles = [...(pageStylesFromDB || []), ...unrecognizedStyles]

            updatePageStyles && updatePageStyles(newPageStyles)

            break
          }
          // When we get a var_click event, publish it upward
          case "var_click": {
            console.log(`got var click`, message.var_name)

            // Editor V3 support
            document.dispatchEvent(
              new CustomEvent(EElementEvent.CLICK_PREVIEW_ELEMENT, {
                detail: {
                  key: message.var_name,
                },
              }) as TClickPreviewElementEvent
            )

            // Editor <= V2 support
            if (setSelectedSubstitutionKey) {
              setSelectedSubstitutionKey(message.var_name)
            }
            break
          }
          /**
           * This post message gets call once on paywall.js setup.
           * Publishing this upwards will tell the editor what to
           * set the initial page styles values to.
           *
           * This also makes sure that any styles that come from
           * the paywall object from the db gets prioritized over
           * the one coming from the device.
           */
          case "page_styles": {
            const unrecognizedStyles =
              message.page_styles.filter((style) => {
                if (!pageStylesFromDB || pageStylesFromDB.length === 0) return style

                return !pageStylesFromDB.find(
                  (s) => s.property === style.property && s.from === style.from
                )
              }) || []
            const newPageStyles = [...(pageStylesFromDB || []), ...unrecognizedStyles]

            updatePageStyles && updatePageStyles(newPageStyles)
          }
          case "has_set_dom": {
            // @ts-ignore
            setTemplateJson(message.data)
          }
        }

        // After the message set local storage
        let existingLocalStorage = localStorage.getItem("paywall_js_messages")
        let existingMessages: any[] = []
        if (existingLocalStorage) {
          existingMessages = JSON.parse(existingLocalStorage) as []
        }
        localStorage.setItem("paywall_js_messages", JSON.stringify([...existingMessages, message]))
      })
    },
    [
      dispatch,
      setPaywallSubs,
      setSubstitutionsFromPing,
      republish,
      updatePageStyles,
      pageStylesFromDB,
      setTemplateJson,
      setSelectedSubstitutionKey,
    ]
  )

  useEffect(() => {
    localStorage.setItem("paywall_js_messages", JSON.stringify([]))
    window.addEventListener("message", handleEvent)
    return () => {
      window.removeEventListener("message", handleEvent)
    }
  }, [handleEvent, dispatch])

  // This will start a timer every time we start loading a new url
  // once it returns, it will see if the timeout key was the same
  // then will set the failure state.
  const [hasTimeout, setHasTimeout] = useState<NodeJS.Timeout | undefined>(undefined)
  useEffect(() => {
    if (
      state.previewState.state === PreviewState.Loading &&
      state.previewState.step === "waiting-for-postmessage" &&
      !state.trackingTimeout
    ) {
      const timeoutKey = nanoid()
      dispatch({
        key: "begin_loading_tracked_timeout",
        timeoutKey,
      })
      if (!hasTimeout) {
        const loaderTimer = setTimeout(() => {
          setHasTimeout(undefined)
          dispatch({
            key: "check_timeout",
            timeoutKey,
          })
        }, 3000)
        // @ts-ignore
        setHasTimeout(loaderTimer)
      }
    }
  }, [dispatch, state.previewState, state.trackingTimeout, hasTimeout])

  const beginLoading = useCallback(() => {
    dispatch({ key: "begin_loading" })
  }, [dispatch])

  const initializeIFrame = useCallback(() => {
    dispatch({
      key: "update_loading_step",
      step: "waiting-for-postmessage",
    })
  }, [dispatch])

  return {
    beginLoading,
    initializeIFrame,
    hasFailed: state.previewState.state === PreviewState.Failed,
  }
}

/**
 * hook to hot-replace webflow.io with superwallcustomer.com
 *
 * @param maybeWebflowUrl potentially contains webflow.io
 * @returns cleaned url with ?transport=editor enabled
 */
export function useWebflowProxy(
  maybeWebflowUrl: string | null | undefined,
  { enableHoverAndClick }: { enableHoverAndClick?: boolean }
) {
  return useMemo<string>(() => {
    if (maybeWebflowUrl == null || maybeWebflowUrl.trim().length === 0) {
      return ""
    }
    try {
      const parsedURL = new URL(maybeWebflowUrl)
      // tells paywall js to communicate over iframe (instead of webkit)
      // see runtime/boot.ts: parseParameters()
      parsedURL.searchParams.append("transport", "editor")
      if (enableHoverAndClick) {
        parsedURL.searchParams.append("enableEditorHover", "true")
      }
      parsedURL.searchParams.append("enableEditor2_5", "true")

      // uses superwall proxy for webflow, strips CSP headers
      if (parsedURL.host.split(".").slice(1).join(".") === "webflow.io") {
        parsedURL.host = `${parsedURL.host.split(".").reverse().pop()}.superwallcustomer.com`
      }

      // Add some random stuff
      parsedURL.searchParams.append("sw-random", nanoid())

      return parsedURL.toString()
    } catch {}
    const paramSeparator = maybeWebflowUrl.includes("?") ? "&" : "?"
    return `${maybeWebflowUrl}${paramSeparator}transport=editor${
      enableHoverAndClick ? "&enableEditorHover=true" : ""
    }`
  }, [maybeWebflowUrl, enableHoverAndClick])
}

export function usePaywallPreviewLifecycle({
  previewUrl,
  setPaywallSubs,
  setSubstitutionsFromPing,
  iFrameRenderKey = "static_render_key",
  subs,
  viewFreeTrial,
  variables,
  enableHoverAndClick,
  pageStyles,
  pageStylesFromDB,
  updatePageStyles,
  setTemplateJson,
  setIframeRef,
  isSubstitutionFocused,
  selectedSubstitutionKey,
  setSelectedSubstitutionKey = (val: string) => {},
  hoverSubstitutionKey,
}: IPaywallPreviewLifecycle) {
  const iFrameSrc = useWebflowProxy(previewUrl, {
    enableHoverAndClick,
  })

  const { iFrameRef, republish, publish } = usePublishToIFrameLifecycle({
    subs,
    viewFreeTrial,
    variables,
    pageStyles: pageStyles,
    setIframeRef,
  })
  const [key, setKey] = useState(null)

  const handleMouseEverEditorElement = (e) => {
    if (e.detail?.key === key) return

    if (e.detail?.key !== key) {
      setKey(e.detail?.key || null)
    }
  }

  useEffect(() => {
    // Event listener when user hovers over an element in the navigator.
    document.addEventListener(EPreviewEvent.MOUSE_OVER_EDITOR_ELEMENT, handleMouseEverEditorElement)

    return () => {
      document.removeEventListener(
        EPreviewEvent.MOUSE_OVER_EDITOR_ELEMENT,
        handleMouseEverEditorElement
      )
    }
  })

  useEffect(() => {
    if (selectedSubstitutionKey) {
      publish({
        event_name: "focus_substitution",
        key: selectedSubstitutionKey,
      })
    } else {
      publish({
        event_name: "unfocus_substitution",
      })
    }
  }, [publish, selectedSubstitutionKey])

  // Editor V3 support
  useEffect(() => {
    if (key) {
      publish({
        event_name: "focus_hovered_substitution",
        key: key as string,
      })
    } else {
      publish({
        event_name: "unfocus_hovered_substitution",
      })
    }
  }, [publish, key])

  // Editor <= V2 support
  useEffect(() => {
    if (hoverSubstitutionKey) {
      publish({
        event_name: "focus_hovered_substitution",
        key: hoverSubstitutionKey as string,
      })
    } else {
      publish({
        event_name: "unfocus_hovered_substitution",
      })
    }
  }, [publish, hoverSubstitutionKey])

  useEffect(() => {
    const eventName = isSubstitutionFocused
      ? "substitution_editor_focused"
      : "substitution_editor_blurred"
    publish({ event_name: eventName })
  }, [publish, isSubstitutionFocused])

  const { beginLoading, initializeIFrame, hasFailed } = useHandleEventsFromIframeLifecycle({
    setPaywallSubs,
    republish,
    updatePageStyles,
    pageStylesFromDB,
    setSubstitutionsFromPing,
    setTemplateJson,
    setSelectedSubstitutionKey,
  })

  // Every time the url changes, we need to rest everything
  useEffect(() => {
    beginLoading()
  }, [iFrameRenderKey, iFrameSrc, beginLoading])

  return {
    iFrameSrc,
    iFrameRef,
    initializeIFrame,
    hasFailed,
  }
}
